import DonorApi from "@/api/DonorApi";
import { BusinessDonor, PrivateDonor } from "@/models/APIModels/Donor";
import BankDetails from "@/models/BankDetails";
import { CardDetailsViewModel } from "@/models/CardDetails";
import {
  BusinessDonorViewModel,
  PrivateDonorViewModel,
} from "@/models/ViewModels/DonorModel";
import PaymentTokenizeUseCase from "./PaymentTokenizeUseCase";
import TokenUseCase from "./TokenUseCase";
import PaymentSourceModel, {
  copyBank,
  copyCard,
} from "@/models/ViewModels/PaymentSourceModel";
import DonorPaymentUseCase from "./DonorPaymentUseCase";
import { PaymentFrequencyEnum } from "@/shared/ModelEnums";

export default class DonorUseCase {
  private readonly _donorApi: DonorApi;

  constructor(donorApi: DonorApi = new DonorApi()) {
    this._donorApi = donorApi;
  }

  async insert(donor: PrivateDonorViewModel | BusinessDonorViewModel) {
    const result = await this._donorApi.insert(
      this.mapDonorFromViewModel(donor),
    );
    if (result.content) {
      const tokenManager = new TokenUseCase();
      await tokenManager.setAccessToken(result.content.accessToken);
    }
    //result.content = this.mapDonorToViewModel(result.content);
    return result;
  }

  async update(
    donor: PrivateDonorViewModel | BusinessDonorViewModel,
    userReference: string,
  ) {
    const result = await this._donorApi.update(
      this.mapDonorFromViewModel(donor),
      userReference,
    );

    if (result.content)
      result.content = this.mapDonorToViewModel(result.content);
    return result;
  }

  async getAll(
    name?: string,
    australianStateCds?: string,
    userStatusCds?: string,
    recognisedSchoolName?: string,
    donorTypeCds?: string,
  ) {
    let queryStringArray: string[] = [];
    if (name) queryStringArray.push(`name=${encodeURIComponent(name)}`);
    if (australianStateCds)
      queryStringArray.push(
        `australianStateCds=${encodeURIComponent(australianStateCds)}`,
      );
    if (userStatusCds)
      queryStringArray.push(
        `userStatusCds=${encodeURIComponent(userStatusCds)}`,
      );
    if (recognisedSchoolName)
      queryStringArray.push(
        `recognisedSchoolName=${encodeURIComponent(recognisedSchoolName)}`,
      );
    if (donorTypeCds)
      queryStringArray.push(`donorTypeCds=${encodeURIComponent(donorTypeCds)}`);

    const result = await this._donorApi.getAll(queryStringArray.join("&"));
    if (result.content)
      result.content = result.content.map((item) =>
        this.mapDonorToViewModel(item),
      );
    return result;
  }

  async getByReference(userReference: string) {
    try {
      const result = await this._donorApi.getByReference(userReference);
      if (result.content) {
        // If no recognized schools are sent back, this stops vue components from crashing
        if (!result.content.recognisedSchool)
          result.content.recognisedSchool = {
            reference: "",
            name: "",
            studentsNum: 0,
            schoolAddresses: [],
          };

        result.content = this.mapDonorToViewModel(result.content);
      }
      return result;
    } catch (error) {
      throw error;
    }
  }

  async submitDonationDetails(
    donor: PrivateDonorViewModel | BusinessDonorViewModel,
    currentDonationState: string,
    cardDetails: CardDetailsViewModel,
    bankDetails: BankDetails,
  ) {
    try {
      switch (currentDonationState) {
        // case "DS_P":
        //   if (donor.donationStatusCd === "DS_I") {
        //     const result = await this._donorApi.deactiveDonations(
        //       this.mapDonorFromViewModel(donor),
        //       donor.userReference
        //     );
        //     if (result.content)
        //       result.content = this.mapDonorToViewModel(result.content);
        //     return result;
        //   }
        //   break;
        case "DS_I":
          if (donor.donationStatusCd === "DS_A") {
            const paymentTokenizeUseCase = new PaymentTokenizeUseCase();
            let paymentSource = new PaymentSourceModel();

            switch (donor.paymentSource!.paymentSourceTypeCd) {
              case "PST_CC":
                paymentSource = copyCard(cardDetails);
                break;
              case "PST_BA":
                paymentSource = copyBank(bankDetails);
                break;
            }
            const inputDonor = this.mapDonorFromViewModel(donor);
            inputDonor.paymentSource =
              await paymentTokenizeUseCase.tokenize(paymentSource);
            const result = await this._donorApi.activateDonations(
              this.mapDonorFromViewModel(inputDonor),
              inputDonor.userReference,
            );
            if (result.content) {
              result.content = this.mapDonorToViewModel(result.content);
              if (result.status === 200) {
                const donorPaymentUseCase = new DonorPaymentUseCase();
                const chargeAccountResult =
                  await donorPaymentUseCase.chargeAccount(
                    result.content.userReference,
                  );
                if (chargeAccountResult.status !== 200) {
                  result.description = chargeAccountResult.description;
                  result.detail = chargeAccountResult.detail;
                  result.detailList = chargeAccountResult.detailList;
                  result.status = chargeAccountResult.status;
                  result.severity = chargeAccountResult.severity;
                  result.exceptionType = chargeAccountResult.exceptionType;
                  result.type = chargeAccountResult.type;
                  result.traceId = chargeAccountResult.traceId;
                  result.instance = chargeAccountResult.instance;
                }
              }
            }
            return result;
          }
          break;
      }
    } catch (error) {
      throw error;
    }
  }

  async updateDonationPayment(
    amount: number,
    donor: PrivateDonorViewModel | BusinessDonorViewModel,
    previousPaymentFrequency: PaymentFrequencyEnum,
  ) {
    try {
      const data = this.mapDonorFromViewModel(donor);
      if (previousPaymentFrequency !== donor.paymentFrequencyCd) {
        data.paymentFrequencyCd = previousPaymentFrequency;
      }
      data.donationAmt = amount;
      const result = await this._donorApi.updateDonationPayment(
        data,
        donor.userReference,
      );
      if (result.content)
        result.content = this.mapDonorToViewModel(result.content);
      return result;
    } catch (error) {
      throw error;
    }
  }

  async updateDonationAccount(
    donor: PrivateDonorViewModel | BusinessDonorViewModel,
    cardDetails?: CardDetailsViewModel,
    bankDetails?: BankDetails,
  ) {
    try {
      const data = this.mapDonorFromViewModel(donor);
      const paymentTokenizeUseCase = new PaymentTokenizeUseCase();
      if (cardDetails) {
        const cardTokenized =
          await paymentTokenizeUseCase.tokenizeCard(cardDetails);
        donor.donorCreditCardToken = cardTokenized.paymentProviderToken;
        data.paymentSource = cardTokenized;
      } else if (bankDetails) {
        const bankTokenized =
          await paymentTokenizeUseCase.tokenizeBankDetails(bankDetails);
        data.paymentSource = bankTokenized;
        donor.donorBankAccount = bankDetails;
      }
      const result = await this._donorApi.updateDonationAccount(
        data,
        data.userReference,
      );
      if (result.content)
        result.content = this.mapDonorToViewModel(result.content);
      return result;
    } catch (error) {
      throw error;
    }
  }

  // async deactivateDonations(
  //   donor: PrivateDonorViewModel | BusinessDonorViewModel
  // ) {
  //   try {
  //     const result = await this._donorApi.deactiveDonations(
  //       this.mapDonorFromViewModel(donor),
  //       donor.userReference
  //     );
  //     if (result.content)
  //       result.content = this.mapDonorToViewModel(result.content);
  //     return result;
  //   } catch (error) {
  //     throw error;
  //   }
  // }

  private mapDonorFromViewModel(
    donor: PrivateDonorViewModel | BusinessDonorViewModel,
  ): PrivateDonor | BusinessDonor {
    return { ...donor };
  }
  private mapDonorToViewModel(
    donor: PrivateDonor | BusinessDonor,
  ): PrivateDonorViewModel | BusinessDonorViewModel {
    return {
      ...donor,
      donationStatusChangeReason: "",
    };
  }
}
